import { apps, SocialApp, socialApps } from '@sso/core-utils';
import { OriginPageData, UrlParams } from '@sso/common-types';
import { type BrandConfig } from '@sso/brand-configs';

import SiblingSet from '../SiblingSet';
import { StyledButton } from './SocialLoginButtons.styles';
import { SocialLogo } from './SocialLogo';
import { useSocialLoginButtons } from './useSocialLoginButtons';

export type Props = {
  page: string;
  urlParams: UrlParams;
  translations: Record<string, string>;
  isSocialForm: boolean;
  trafficPrefix: string;
  market: string;
  realm: string;
  isChina: boolean;
  brandConfig: BrandConfig;
  originPageData: OriginPageData;
  socialLoginLaunch: (app: string) => void;
  orientation: 'horizontal' | 'vertical';
  enableApple?: boolean;
  orderedSocialApps?: Array<SocialApp>;
};

export const fullTextMap = {
  [socialApps.amazon]: 'amazon',
  [socialApps.facebook]: 'continueWithFacebook',
  [socialApps.google]: 'continueWithGoogle',
  [socialApps.wechat]: 'signInWithWechat',
  [socialApps.apple]: 'apple',
};

const narrowApp = (value: string): value is keyof typeof fullTextMap => {
  return value in fullTextMap;
};

export const SocialLoginButtons = ({
  page,
  urlParams,
  translations,
  isSocialForm,
  trafficPrefix,
  market,
  realm,
  isChina,
  brandConfig,
  originPageData,
  socialLoginLaunch,
  orientation,
  enableApple = false,
  orderedSocialApps = Object.values(socialApps),
}: Props) => {
  const { buttonsVisibility, handleButtonClick } = useSocialLoginButtons({
    page,
    market,
    realm,
    isChina,
    brandConfig,
    urlParams,
    enableApple,
    originPageData,
    socialLoginLaunch,
  });

  const { signInWith = '', createAccountUsing = '', signupContinueWithSocial = '' } = translations;
  const buttonCount = Object.keys(buttonsVisibility).reduce(
    (acc, key) => (buttonsVisibility[key as SocialApp] === true ? acc + 1 : acc),
    0,
  );
  const iconOnly = buttonCount >= 3 && page !== 'create';
  const fullText = !(buttonCount > 1);
  return (
    <SiblingSet stretch gap='md' orientation={orientation}>
      {orderedSocialApps.map((app: SocialApp) => {
        if (buttonsVisibility[app] === true && narrowApp(app)) {
          // historially, social login doesn't default to www, but some central code is
          // setting app to www if omitted, this lets server side decide default. we keep
          // app www if there is a specified path, otherwise assume it was defaulted
          const urlQuery = { ...urlParams };

          if (urlQuery != null && urlQuery.app === apps.WWW) {
            // don't want to break this logic while introducing the rule - please fix if touching this code
            // eslint-disable-next-line eqeqeq
            if (urlQuery.path == null || urlQuery.path == '') {
              urlQuery.app = undefined;
            }
          }

          const altText =
            page !== 'create'
              ? signInWith.replace('{APP}', translations[app] ?? '')
              : createAccountUsing.replace('{APP}', translations[app] ?? '');
          const appName = app.charAt(0).toUpperCase() + app.slice(1);

          const defaultText = fullText ? translations[fullTextMap[app] ?? ''] : translations[app];
          const buttonText = isSocialForm ? signupContinueWithSocial.replace('{app_or_email}', appName) : defaultText;

          return (
            <StyledButton
              $iconOnly={iconOnly}
              onClick={() => handleButtonClick(app)}
              key={app}
              id={`${app}_auth`}
              design='secondary'
              data-eid={`${trafficPrefix}.social_login.${app}.button`}
              target='_top'
              aria-label={altText}
              text={iconOnly ? '' : buttonText}
              icon={<SocialLogo app={app} />}
              data-testid={`${app}_auth`}
            />
          );
        }
        return null;
      })}
    </SiblingSet>
  );
};
